import { adminFetch, buildListParams, normalizeList } from "./_client";
import type { TimelineEvent, AdminUser, UserNote, UserDetailData, AuditLogEntry, ImpersonationSessionEntry, AdminListParams, AdminListResponse, User360Response, User360RiskResponse, User360DevicesResponse, User360MessagesResponse, User360ConversationMessagesResponse, User360ReviewsResponse, User360CsEvent, User360Payment, User360Credit, User360CreditsResponse, UserEnforcementResponse } from "./types";


export async function fetchAdminUsers(token: string, params: AdminListParams = {}): Promise<AdminListResponse<AdminUser>> {
  const sp = buildListParams(params);
  const raw = await adminFetch<Record<string, unknown>>(`/admin/users?${sp}`, token);
  return normalizeList<AdminUser>(raw, "users");
}


export function fetchAdminUser(token: string, id: string) {
  return adminFetch<UserDetailData>(`/admin/users/${id}`, token);
}


/**
 * Task #7387 — fetch a single page of trips for the admin user profile.
 * Page is 0-based; page 0 is already loaded as part of the initial
 * `fetchAdminUser` call, so callers should start at page 1 for "Load more".
 * Returns only the trips slice and the total count so callers can decide
 * whether to keep showing the button.
 */
export async function fetchAdminUserTripsPage(
  userId: string,
  token: string,
  page: number,
): Promise<{ trips: import("./types").UserDetailTrip[]; trips_total: number }> {
  const data = await adminFetch<UserDetailData>(`/admin/users/${userId}?tripsPage=${page}`, token);
  return { trips: data.trips, trips_total: data.trips_total ?? data.trips.length };
}


/**
 * Fetch a single page of billing rows for the admin user profile, mirroring
 * `fetchAdminUserTripsPage`. Page is 0-based with a page size of 20; callers
 * append pages until `billing.length` reaches `billing_total`.
 */
export async function fetchAdminUserBillingPage(
  userId: string,
  token: string,
  page: number,
): Promise<{ billing: import("./types").UserBillingEntry[]; billing_total: number }> {
  const data = await adminFetch<UserDetailData>(`/admin/users/${userId}?billingPage=${page}`, token);
  return { billing: data.billing, billing_total: data.billing_total ?? data.billing.length };
}


export function fetchUserActivity(token: string, id: string, limit = 50) {
  return adminFetch<{ events: TimelineEvent[] }>(`/admin/users/${id}/activity?limit=${limit}`, token);
}


export interface AccountLifecycleResult {
  success: boolean;
  listings?: {
    ok: boolean; carIds?: number[]; restored?: number[];
    skipped?: { carId: number; reason: string }[];
    failed?: { carId: number; reason: string }[];
    searchFailed?: number[]; error?: string;
  };
}
export function reconcileUserListings(token: string, id: string) {
  return adminFetch<AccountLifecycleResult>(`/admin/users/${id}/reconcile-listings`, token, { method: "POST" });
}
export function suspendUser(token: string, id: string, reason: string) {
  return adminFetch<AccountLifecycleResult>(`/admin/users/${id}/suspend`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function unsuspendUser(token: string, id: string, reason?: string) {
  // Optional context for why the account was reinstated — recorded on the
  // user's internal notes and the audit trail server-side.
  return adminFetch<AccountLifecycleResult>(`/admin/users/${id}/unsuspend`, token, {
    method: "POST",
    ...(reason && reason.trim() ? { body: JSON.stringify({ reason: reason.trim() }) } : {}),
  });
}


// Current enforcement status (suspended/banned + when/by whom/why + pinned
// notes) plus the full warn/suspend/unsuspend/ban timeline for one user.
export function fetchUserEnforcement(token: string, id: string) {
  return adminFetch<UserEnforcementResponse>(`/admin/users/${id}/enforcement`, token);
}


// Task #5504 — admin "Edit profile". Correct a user's first name, last
// name, phone, and/or email without impersonation. Only the supplied fields
// are changed; `reason` (5+ chars) is required and audit-logged. An email
// change is duplicate-checked (409) and synced to the auth provider server-
// side.
export interface EditUserProfileInput {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  email?: string;
  reason: string;
}

export function editUserProfile(token: string, id: string, input: EditUserProfileInput) {
  return adminFetch<{ success: boolean }>(`/admin/users/${id}/edit-profile`, token, {
    method: "POST",
    body: JSON.stringify(input),
  });
}


// ─── Host profile viewer / editor (Task #7827) ────────────────────────────

/** Fetch the live host profile fields for a user. */
export function fetchAdminHostProfile(token: string, userId: string) {
  return adminFetch<import("./types").AdminHostProfile>(`/admin/users/${userId}/host-profile`, token);
}

export interface UpdateAdminHostProfileInput {
  about?: string | null;
  host_bio?: string | null;
  car_story?: string | null;
  auto_experience?: string | null;
  specializations?: string[] | null;
  certifications?: string[] | null;
  cover_image?: string | null;
  profile_image?: string | null;
  reason: string;
}

/** Write changed host profile fields and record an audit_log row. */
export function updateAdminHostProfile(token: string, userId: string, input: UpdateAdminHostProfileInput) {
  return adminFetch<{ success: boolean; profile: import("./types").AdminHostProfile | null }>(
    `/admin/users/${userId}/host-profile`,
    token,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}


export function deleteUser(token: string, id: string, reason: string) {
  return adminFetch<{ success: boolean; searchSyncPending?: number[] }>(`/admin/users/${id}/delete`, token, {
    method: "POST",
    body: JSON.stringify({ reason, confirmListingsRemoved: true }),
  });
}
export function reconcileAccountRemoval(token: string, id: string) {
  return adminFetch<{ success: boolean; reconciled: number[]; failed: number[] }>(
    `/admin/users/${id}/reconcile-removal`, token, { method: "POST" },
  );
}


// Task #2451 — admin counterparts to the soft-delete action.
// `restoreUser` undoes a soft-delete (clears deleted_at) and only works
// while the row is still inside the 30-day GDPR retention window.
// `purgeUserNow` immediately runs the same anonymization the inngest
// retention job runs on day 30; it is gated server-side on the user
// having no active trips and writes audit_log.
export function restoreUser(token: string, id: string, reason: string) {
  return adminFetch<{ success: boolean }>(`/admin/users/${id}/restore`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function purgeUserNow(token: string, id: string, reason: string) {
  return adminFetch<{ success: boolean }>(`/admin/users/${id}/purge-now`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function impersonateUser(token: string, id: string, reason: string) {
  return adminFetch<{ success: boolean; sessionId: string; expiresAt: number; user: { id: string; email: string; name: string } }>(`/admin/users/${id}/impersonate`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function fetchImpersonationSessions(
  token: string,
  page = 1,
  filters: { adminUserId?: string; targetUserId?: string; dateFrom?: string; dateTo?: string; status?: "" | "active" | "ended" } = {},
) {
  const params = new URLSearchParams({ page: String(page) });
  if (filters.adminUserId) params.set("admin_user_id", filters.adminUserId);
  if (filters.targetUserId) params.set("target_user_id", filters.targetUserId);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.status) params.set("status", filters.status);
  return adminFetch<{ sessions: ImpersonationSessionEntry[]; total: number; page: number; limit: number }>(
    `/admin/impersonation-sessions?${params}`, token,
  );
}


export function fetchImpersonationSessionActions(token: string, sessionId: string) {
  return adminFetch<{ session: ImpersonationSessionEntry; actions: AuditLogEntry[] }>(
    `/admin/impersonation-sessions/${encodeURIComponent(sessionId)}/actions`, token,
  );
}


export function endImpersonation(token: string, sessionId: string, targetUserId: string) {
  return adminFetch<{ success: boolean }>("/admin/impersonate/end", token, {
    method: "POST",
    body: JSON.stringify({ sessionId, targetUserId }),
  });
}


export function addUserNote(
  token: string,
  id: string,
  body: { note: string; mentions?: string[]; parent_note_id?: number | null } | string,
) {
  const payload = typeof body === "string" ? { note: body } : body;
  return adminFetch<UserNote>(`/admin/users/${id}/notes`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}


export function updateUserNote(
  token: string,
  id: string,
  noteId: number,
  body: { note: string; mentions?: string[] } | string,
) {
  const payload = typeof body === "string" ? { note: body } : body;
  return adminFetch<UserNote>(`/admin/users/${id}/notes/${noteId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}


export function fetchUser360(token: string, id: string) {
  return adminFetch<User360Response>(`/admin/users/${id}/360`, token);
}

export function fetchUser360Risk(token: string, id: string) {
  return adminFetch<User360RiskResponse>(`/admin/users/${id}/risk`, token);
}

export function fetchUser360Devices(token: string, id: string) {
  return adminFetch<User360DevicesResponse>(`/admin/users/${id}/devices`, token);
}

export function fetchUser360Messages(token: string, id: string) {
  return adminFetch<User360MessagesResponse>(`/admin/users/${id}/messages`, token);
}

// Task #5503 — full message thread for one conversation, fetched lazily when an
// admin expands a row in the Messages tab.
export function fetchUser360ConversationMessages(token: string, id: string, conversationId: string | number) {
  return adminFetch<User360ConversationMessagesResponse>(
    `/admin/users/${id}/conversations/${conversationId}/messages`,
    token,
  );
}

export function fetchUser360Reviews(token: string, id: string) {
  return adminFetch<User360ReviewsResponse>(`/admin/users/${id}/reviews`, token);
}

export function fetchUser360CsInteractions(token: string, id: string, limit = 20) {
  // Task #3705 — `partial` lists which CS sources failed to load so the strip
  // can warn instead of silently dropping a feed.
  return adminFetch<{ events: User360CsEvent[]; partial?: string[] }>(`/admin/users/${id}/cs-interactions?limit=${limit}`, token);
}

// Task #6360 — PAYOUT setup status: host Stripe Connect onboarding health,
// loaded with the Payments panel so the Stripe lookup only runs on demand.
// `applies=false` (or absent) ⇒ hide the status entirely.
export interface PayoutSetupSummary {
  applies: boolean;
  state: "enabled" | "not_enabled" | "no_account" | "unknown" | null;
  reason: string | null;
}

export function fetchUser360Payments(token: string, id: string, limit = 100) {
  return adminFetch<{
    payments: User360Payment[];
    payout_setup?: PayoutSetupSummary;
    stripe_ext_account_id?: string | null;
    stripe_connect_region?: string | null;
    partial?: string[];
  }>(`/admin/users/${id}/payments?limit=${limit}`, token);
}

export function fetchUser360Credits(token: string, id: string) {
  return adminFetch<User360CreditsResponse>(`/admin/users/${id}/credits`, token);
}


export function resetUserPassword(token: string, id: string) {
  return adminFetch<{ success: boolean; email: string }>(
    `/admin/users/${id}/reset-password`, token, { method: "POST" },
  );
}


export function resetUserMfa(token: string, id: string) {
  return adminFetch<{
    success: boolean;
    factors_deleted: number;
    recovery_codes_cleared: number | null;
    verified_factors_remaining: number;
  }>(`/admin/users/${id}/reset-mfa`, token, { method: "POST" });
}


export function resendUserVerification(token: string, id: string) {
  return adminFetch<{ success: boolean; email: string }>(
    `/admin/users/${id}/resend-verification`, token, { method: "POST" },
  );
}


export function issueUserCredit(
  token: string,
  id: string,
  body: { amount_cents: number; currency?: string; kind?: string; description?: string | null; expires_at?: string | null; trip_id?: number | null },
) {
  return adminFetch<{ credit: User360Credit }>(`/admin/users/${id}/issue-credit`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function forceUserLogout(token: string, id: string) {
  return adminFetch<{ success: boolean }>(`/admin/users/${id}/force-logout`, token, { method: "POST" });
}


export function sendUserMessage(token: string, id: string, content: string, templateId?: string | null) {
  return adminFetch<{
    conversation_public_id: string;
    message: { id: number; content: string; created_at: string };
  }>(`/admin/users/${id}/send-message`, token, {
    method: "POST",
    body: JSON.stringify({ content, template_id: templateId ?? null }),
  });
}


export function mergeUserAccounts(
  token: string,
  loserId: string,
  body: { winner_user_id: string; reason?: string },
) {
  return adminFetch<{ success: boolean; merge: Record<string, unknown> | null;
    countryConsistency?: { outcome: string; winner: { status: string; reason: string };
      loser: { status: string; reason: string } } }>(
    `/admin/users/${loserId}/merge`, token, { method: "POST", body: JSON.stringify(body) },
  );
}
